import {Checkbox, IconButton, SelectField} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useParams} from 'react-router';

import {useProjectFieldsQuery} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {
  displayKey,
  getDefaultAbsoluteDateValue,
  getKeyAllowedOps,
  isValidFilter,
} from '../../util/filters';
import {EMPTY_FILTER_VALUE} from '../../util/filtersDefaults';
import {
  type FilterKey,
  type FilterMeta,
  type IndividualFilter,
  type IndividualOp,
} from '../../util/filterTypes';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {FilterTagSelectField} from '../InfiniteScrollDropdown/FilterTagSelectField';
import {getCurrentSelections} from '../InfiniteScrollDropdown/utils';
import {getProjectFieldsQueryVariables} from '../ProjectFieldSelector';
import {useEditableFilterContext} from './FilterContext';
import {type FilterKeySelectorCreatorProps} from './FilterKeySelector';
import {type FilterValueSelectorCreatorProps} from './FilterValueSelector';
import {InvalidFilterTooltip} from './InvalidFilterTooltip';

const TAG_KEY_PLACEHOLDER = 'tags:-';

const TAG_OP_OPTIONS = [
  {id: '=', label: 'is'},
  {id: '!=', label: 'is not'},
  {id: 'IN', label: 'in'},
  {id: 'NIN', label: 'not in'},
] as const;

// Every row, tag or not, is one flex line: fixed-size checkbox, leading
// content and delete button, intrinsic key and operator fields, and a value
// field that grows into what is left (capped by the field's own max width).
const ROW_CLASS = 'flex items-center gap-8';

const OperatorUserLabels = new Map<IndividualOp, string>([
  ['=', '='],
  ['!=', '\u2260'],
  ['>=', '\u2265'],
  ['<=', '\u2264'],
  ['IN', 'IN'],
  ['NIN', 'NOT IN'],
  ['CONTAINS', 'CONTAINS'],
  ['WITHINSECONDS', 'within last'],
]);

interface FilterEditableProps {
  filter: IndividualFilter;
  filterIndex: number;
  justAdded: boolean;

  /** Optional content at the start of the row (e.g. "WHERE" or AND/OR dropdown). When set, checkbox is shown to the left of the leading content. */
  leadingContent?: React.ReactNode;

  setFilter(filter: IndividualFilter): void;
  setFilterOp(op: IndividualOp): void;
  setFilterValue(value: RunTypes.Value): void;
  setFilterMultiValue(value: RunTypes.Value[]): void;
  setFilterMeta(meta: FilterMeta): void;
  setFilterDisabled(disabled: boolean): void;
  deleteFilter(): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: FilterValueSelectorCreatorProps<FilterKey>
  ): React.ReactNode;
}

const getOperatorDisplayList = (
  filterKey: FilterKey,
  keyType: string | undefined
) =>
  getKeyAllowedOps(filterKey, keyType).map(op => {
    const opLabel = OperatorUserLabels.get(op);
    if (!opLabel) {
      throw new Error('OperatorUserLabels missing op: ' + op);
    }
    return {id: op, label: opLabel};
  });

export const FilterEditable = ({
  filter,
  justAdded,
  leadingContent,
  setFilter,
  setFilterOp,
  setFilterDisabled,
  deleteFilter,
  filterKeySelector,
  filterValueSelector,
  filterIndex,
}: FilterEditableProps) => {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const [{isMultiValueFilter}, filterActions] =
    useEditableFilterContext(filterIndex);
  const gqlVars = getProjectFieldsQueryVariables({
    entityName: entityName,
    projectName: projectName,
    pattern: filter.key.name, // we only want to get the key type for the current filter's key. it may or may not have been queried already by the ProjectFieldSelector.
  });
  const {data} = useProjectFieldsQuery({
    variables: gqlVars,
    context: propagateErrorsContext(), // we don't care about error or loading here, it's ok if we don't know the key type.
  });
  const filterKeyType = data?.project?.fields?.edges?.[0]?.node?.type;

  const operatorDisplayList = getOperatorDisplayList(filter.key, filterKeyType);

  const isValid = isValidFilter(filter);
  const invalidHintId = React.useId();

  // Rows repeat the same controls, so each one is a named group and its
  // controls carry the row's number and key: "Filter 2: loss",
  // "Enable filter 2: loss", "Delete filter 2: loss".
  const keyLabel =
    filter.key.section === 'tags' ? 'tags' : displayKey(filter.key);
  const rowDescriptor =
    keyLabel === '-' ? `${filterIndex + 1}` : `${filterIndex + 1}: ${keyLabel}`;
  const rowGroupProps = {
    role: 'group',
    'aria-label': `Filter ${rowDescriptor}`,
    'aria-describedby': isValid ? undefined : invalidHintId,
  } as const;

  const checkboxCell = (
    <div className="flex h-24 w-24 shrink-0 items-center justify-center">
      {!isValid ? (
        <InvalidFilterTooltip id={invalidHintId} />
      ) : (
        <Checkbox
          aria-label={`Enable filter ${rowDescriptor}`}
          data-test="filter-toggle"
          isSelected={!filter.disabled}
          // Only toggle if all fields are set.
          onChange={() => setFilterDisabled(!filter.disabled)}
        />
      )}
    </div>
  );

  // The field reports the whole selection after every change. Individual
  // tag filters hold one tag; multi-value filters (IN / NIN) hold a list.
  const setTagSelections = React.useCallback(
    (next: string[]) => {
      if (isMultiValueFilter) {
        filterActions.setFilterValueMulti(next);
      } else {
        filterActions.setFilterValueIndividual(next[0] ?? '');
      }
    },
    [filterActions, isMultiValueFilter]
  );
  const isV2 = leadingContent != null;
  const leadingCell = isV2 ? (
    <div className="w-[100px]">{leadingContent}</div>
  ) : (
    checkboxCell
  );

  // A tag filter keeps the tag in the key (= and !=) or in the value (IN and
  // NIN), and changing the operator resets both, so the row owns its operator
  // and value fields instead of going through `filterValueSelector`.
  if (filter.key.section === 'tags') {
    // we are hardcoding the keyValue and storedKey to keep the selector from losing context
    // when I clear out the values: https://weightsandbiases.slack.com/archives/C041GF3LB1Q/p1692367577594179
    const tagKeyColumn = filterKeySelector({
      focusOnMount: justAdded,
      keyValue: TAG_KEY_PLACEHOLDER,
      storedKey: TAG_KEY_PLACEHOLDER,
      onValidSelection: keyString => {
        // when the key is changed we disable the filter automatically and default it
        const filterKey = Run.keyFromString(keyString);
        if (filterKey != null) {
          setFilter({
            ...filter,
            key: filterKey,
            op: '=',
            value: true,
            disabled: true,
          });
        }
      },
    });

    return (
      <Tailwind>
        <div {...rowGroupProps} className={ROW_CLASS} data-test="filter-item">
          {isV2 && checkboxCell}
          {leadingCell}
          {tagKeyColumn}
          <SelectField
            aria-label="Operator"
            data-test="filter-operation"
            items={TAG_OP_OPTIONS}
            value={filter.op}
            onChange={op => {
              if (op != null) {
                // @ts-ignore
                filterActions.changeFilterOperation(op);
              }
            }}
          />
          <FilterTagSelectField
            className="max-w-[600px]"
            data-test="filter-value"
            entityName={entityName}
            projectName={projectName}
            isMultiSelect={isMultiValueFilter}
            // @ts-ignore safe, but revisit to figure out why overlap isn't happening correctly
            selections={getCurrentSelections(filter)}
            onSelectionsChange={setTagSelections}
          />
          <IconButton
            variant="ghost"
            icon="delete"
            data-test="filter-delete"
            aria-label={`Delete filter ${rowDescriptor}`}
            onPress={deleteFilter}
          />
        </div>
      </Tailwind>
    );
  }

  return (
    <Tailwind>
      <div {...rowGroupProps} data-test="filter-item" className={ROW_CLASS}>
        {isV2 && checkboxCell}
        {leadingCell}
        {filterKeySelector({
          isValid,
          focusOnMount: justAdded,
          keyValue: displayKey(filter.key),
          storedKey: displayKey(filter.key),
          onValidSelection: keyString => {
            const filterKey = Run.keyFromString(keyString);
            if (filterKey != null) {
              if (
                filterKey.section === 'run' &&
                filterKey.name === 'createdAt'
              ) {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '<=',
                  value: getDefaultAbsoluteDateValue(),
                  disabled: false,
                });
              } else if (filterKey.section === 'tags') {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '=',
                  value: true,
                  disabled: true,
                });
              } else {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '=',
                  value: EMPTY_FILTER_VALUE,
                  disabled: true,
                });
              }
            }
          },
        })}
        <SelectField
          aria-label="Operator"
          data-test="filter-operation"
          items={operatorDisplayList}
          placeholder="operator"
          isInvalid={!isValid}
          value={filter.op}
          onChange={op => {
            if (op != null) {
              setFilterOp(op as IndividualOp);
            }
          }}
        />
        {filterValueSelector({
          filter,
          setFilter,
          isValid,
        })}
        <IconButton
          variant="ghost"
          icon="delete"
          data-test="filter-delete"
          aria-label={`Delete filter ${rowDescriptor}`}
          onPress={deleteFilter}
        />
      </div>
    </Tailwind>
  );
};

export default React.memo(FilterEditable);
